import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Home, RefreshCw, ArrowLeft } from 'lucide-react';

interface ErrorPageProps {
  errorCode: '404' | '403' | '500' | '503';
  title: string;
  message: string;
  imagePath: string;
  showRefresh?: boolean;
}

const ErrorPage: React.FC<ErrorPageProps> = ({
  errorCode,
  title,
  message,
  imagePath,
  showRefresh = false
}) => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          {/* Error Code */}
          <motion.h1
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="text-8xl md:text-9xl font-bold text-[#FDB913] mb-4"
          >
            {errorCode}
          </motion.h1>

          {/* Food Image */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mx-auto mb-8 relative"
          >
            <img
              src={imagePath}
              alt="Error illustration"
              className="w-64 h-64 md:w-80 md:h-80 object-contain mx-auto drop-shadow-2xl"
            />
            {/* Floating animation */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0"
            />
          </motion.div>

          {/* Title */}
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-3xl md:text-4xl font-bold text-white mb-4"
          >
            {title}
          </motion.h2>

          {/* Message */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-lg md:text-xl text-white/80 mb-8 max-w-2xl mx-auto"
          >
            {message}
          </motion.p>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            {/* Go Back Button */}
            <button
              onClick={handleGoBack}
              className="px-6 py-3 bg-white/10 backdrop-blur-md text-white rounded-lg font-medium hover:bg-white/20 transition-all duration-200 flex items-center gap-2 border border-white/20"
            >
              <ArrowLeft className="w-5 h-5" />
              Go Back
            </button>

            {/* Home Button */}
            <button
              onClick={handleGoHome}
              className="px-6 py-3 bg-[#FDB913] text-black rounded-lg font-bold hover:bg-[#fdc935] transition-all duration-200 flex items-center gap-2 shadow-lg"
            >
              <Home className="w-5 h-5" />
              Go to Homepage
            </button>

            {/* Refresh Button (for 500/503 errors) */}
            {showRefresh && (
              <button
                onClick={handleRefresh}
                className="px-6 py-3 bg-white/10 backdrop-blur-md text-white rounded-lg font-medium hover:bg-white/20 transition-all duration-200 flex items-center gap-2 border border-white/20"
              >
                <RefreshCw className="w-5 h-5" />
                Try Again
              </button>
            )}
          </motion.div>

          {/* Additional Help Text */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-12 text-white/60 text-sm"
          >
            <p>Need help? Contact support at support@dineai.ai</p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default ErrorPage;